import { StylePace } from 'api/video';
import { LogoPlace } from 'types/adEditorTypes';

import { VideoScene, VideoTemplateStyle } from './types';

/**
 * SYM-6265. The simple Scenes tab, and what sits behind its Advanced switch.
 *
 * Steven, 18 September 2026: "the app is called sympl. But we have added so
 * much possibilities that video track 1 is not sympl anymore." The look
 * already decides how long a slide lasts and how it changes to the next, so
 * offering both on every slide repeated a choice that had been made. They are
 * folded away behind ONE switch for the whole tab, together with where the
 * logo sits.
 *
 * Three rules this file holds, so the wizard does not have to:
 * - The switch is a VIEW. Turning it off hides the controls and resets nothing.
 * - Nothing in force is ever hidden: a slide whose length or transition was set
 *   by hand says so on the simple tab, and so does a logo that is not top
 *   right. Clicking that marker is what turns the switch on.
 * - "Back to the look's setting" undoes a slide's hand-set values, and only
 *   that slide's.
 */

/**
 * SYM-6353. The four places of the image-ad logo row (SYM-6301), top middle
 * included, drawn by the same shared row (components/logo-place/
 * LogoPlaceButtons.tsx). It stays behind Advanced on Scenes (Steven's hand
 * test on QA, 27 Sep 2026, moved it back there from the Style step).
 */
export type LogoPosition = LogoPlace;

export const DEFAULT_LOGO_POSITION: LogoPosition = 'top-right';

/** What the draft says, or top right for anything else (as the server reads it). */
export const logoPositionFrom = (value: unknown): LogoPosition =>
  value === 'top-left' || value === 'top-middle' || value === 'none'
    ? value
    : DEFAULT_LOGO_POSITION;

export const logoIsSetByHand = (position: LogoPosition): boolean =>
  position !== DEFAULT_LOGO_POSITION;

/** What the marker on the simple tab says about a logo moved by hand, or null. */
export const logoMarker = (position: LogoPosition): string | null => {
  if (position === 'none') return 'No logo';
  if (position === 'top-left') return 'Logo top left';
  if (position === 'top-middle') return 'Logo top middle';
  return null;
};

export const sceneIsSetByHand = (scene: VideoScene): boolean =>
  scene.durationSetByHand === true || scene.transitionSetByHand === true;

/** What a marker on the simple tab says about a slide, or null for none. */
export function handSetNote(scene: VideoScene): string | null {
  const length = scene.durationSetByHand === true;
  const change = scene.transitionSetByHand === true;
  if (length && change) return 'Length and transition set by hand';
  if (length) return 'Length set by hand';
  if (change) return 'Transition set by hand';
  return null;
}

type StylePaces = Partial<Record<VideoTemplateStyle, StylePace>> | null;

/**
 * The transition the look gives the slide at `position` (0 = first slide), in
 * the wizard's own four words, or null when it has none to show: the first
 * slide never has one, and a server that sent no order leaves the buttons on
 * whatever the slide carries.
 *
 * The server names directions (slide-left, slide-up, ...); the wizard only has
 * "Slide", so every direction reads as that. The direction itself is the
 * look's business and is drawn from the server's plan, not from here.
 */
export function lookTransitionFor(
  style: VideoTemplateStyle,
  position: number,
  stylePaces: StylePaces | undefined
): VideoScene['transition'] | null {
  if (position < 1) return null;
  const order = stylePaces?.[style]?.transitions;
  if (!Array.isArray(order) || order.length === 0) return null;

  const name = order[(position - 1) % order.length];
  if (typeof name !== 'string') return null;
  if (name === 'fade') return 'fade';
  return name.startsWith('slide') ? 'slide' : null;
}

/** The transition to show as chosen for a slide: its own when set by hand, else the look's. */
export function shownTransition(
  scene: VideoScene,
  style: VideoTemplateStyle,
  position: number,
  stylePaces: StylePaces | undefined
): VideoScene['transition'] {
  if (scene.transitionSetByHand === true) return scene.transition;
  return lookTransitionFor(style, position, stylePaces) ?? scene.transition;
}

/**
 * One slide handed back to its look: the by-hand marks come off, the length
 * becomes the look's pace (when the server told us one) and the transition the
 * look's for that position (when it told us that).
 */
export function backToLook(
  scene: VideoScene,
  style: VideoTemplateStyle,
  position: number,
  stylePaces: StylePaces | undefined
): VideoScene {
  const pace = stylePaces?.[style]?.slide_duration;
  const transition = lookTransitionFor(style, position, stylePaces);

  return {
    ...scene,
    durationSetByHand: false,
    transitionSetByHand: false,
    transitionAsStored: undefined,
    ...(typeof pace === 'number' && pace > 0 ? { duration: pace } : {}),
    ...(transition ? { transition } : {}),
  };
}

/**
 * Whether pressing a transition button changes anything. The button the look
 * already lights up, on a slide nobody set by hand, is not a choice: taking it
 * as one would replace the look's direction (say, slide-up) by the wizard's
 * single word "slide", which the server reads as slide-left.
 */
export function transitionClickIsAChoice(
  scene: Pick<VideoScene, 'transition' | 'transitionSetByHand'>,
  shown: string | null | undefined,
  pressed: string
): boolean {
  if (scene.transitionSetByHand === true) return true;
  return (shown ?? scene.transition) !== pressed;
}
